import React from 'react';
import PropTypes from 'prop-types';
import format from 'date-fns/format';

import {
    Button,
    IconFlag,
    LabeledRatingStars,
    RatingStars,
    Spacer,
    Text,
} from '@zillow/constellation';

import EditReviewResponse from 'app/shared/components/EditReviewResponse';
import ReportReview from 'app/shared/components/ReportReview';

import StringConstants from 'app/shared/constants/StringConstants';
import ReviewsConstants from 'app/shared/constants/ReviewsConstants';

// Stylesheets
import './style.scss';

const propTypes = {
    review: PropTypes.shape({
        id: PropTypes.string.isRequired,
        reviewType: PropTypes.string.isRequired,
        reviewResponseId: PropTypes.string,
        starLevel: PropTypes.number.isRequired,
        reviewMessage: PropTypes.string.isRequired,
        reviewStatus: PropTypes.string.isRequired,
        brand: PropTypes.string.isRequired,
        createdIsoString: PropTypes.string.isRequired,
        responseMessage: PropTypes.string,
        managerReportedStatus: PropTypes.string,
    }).isRequired,
    onSubmitResponse: PropTypes.func.isRequired,
    onReportReview: PropTypes.func.isRequired,
    canEdit: PropTypes.bool.isRequired,
};

const defaultProps = {
};

function formatDate(date) {
    return `${format(new Date(date), 'MMM D, YYYY')}`;
}

class Review extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            isResponding: false,
            isReporting: false,
        };
        this.handleToggleResponding = this.handleToggleResponding.bind(this);
        this.handleToggleReporting = this.handleToggleReporting.bind(this);
        this.handleReport = this.handleReport.bind(this);
        this.handleSubmitResponse = this.handleSubmitResponse.bind(this);
    }

    handleSubmitResponse(message) {
        const { id, reviewType, reviewResponseId } = this.props.review;
        return this.props.onSubmitResponse(id, reviewType, message, reviewResponseId)
            .then(() => this.setState({ isResponding: false }));
    }

    handleReport(reason, comment) {
        const { id, reviewType } = this.props.review;
        return this.props.onReportReview(id, reviewType, reason, comment)
            .then(() => this.setState({ isReporting: false }));
    }

    handleToggleResponding() {
        this.setState({ isResponding: !this.state.isResponding });
    }

    handleToggleReporting() {
        if (this.props.review.managerReportedStatus !== ReviewsConstants.REPORT_REVIEW_STATUS.PENDING) {
            this.setState({ isReporting: !this.state.isReporting });
        }
    }

    renderReview() {
        const { review } = this.props;
        const {
            starLevel, reviewMessage, createdIsoString, brand, reviewType,
        } = review;
        return (
            <div>
                <LabeledRatingStars
                    ratingStars={<RatingStars value={starLevel} aria-label={`${starLevel} out of 5 stars`} />}
                    label={<Text fontType="bodyHeading">{ReviewsConstants.REVIEW_TYPES_LABEL[reviewType]}</Text>}
                />
                {reviewMessage && (
                    <Spacer paddingTop="xs" data-test="message">
                        <Text fontType="body">{reviewMessage}</Text>
                    </Spacer>
                )}
                <Spacer paddingTop="xs">
                    <Text fontType="body" fontColor="gray400">
                        {`Posted on ${StringConstants.FORMATTED_BRAND_NAMES[brand]}: ${formatDate(createdIsoString)}`}
                    </Text>
                </Spacer>
            </div>
        );
    }

    renderReportReviewForm() {
        return (
            <Spacer paddingTop="xs">
                <ReportReview
                    onCancel={this.handleToggleReporting}
                    onSubmit={this.handleReport}
                />
            </Spacer>
        );
    }

    renderResponseForm() {
        return (
            <Spacer paddingTop="xs">
                <EditReviewResponse
                    mode="create"
                    onSubmit={this.handleSubmitResponse}
                    onCancel={this.handleToggleResponding}
                />
            </Spacer>
        );
    }

    renderRespondButton() {
        const { responseMessage } = this.props.review;
        if (responseMessage) {
            return null;
        }
        return (
            <Spacer paddingTop="sm">
                <Button
                    buttonType="primary"
                    onClick={this.handleToggleResponding}
                >
                    Respond
                </Button>
            </Spacer>
        );
    }

    renderReportButton() {
        const { managerReportedStatus } = this.props.review;
        const isPending = managerReportedStatus === ReviewsConstants.REPORT_REVIEW_STATUS.PENDING;
        return (
            <div
                className="Review-report-container"
                onClick={this.handleToggleReporting}
            >
                <IconFlag
                    className={isPending ? 'Review-report-disabled' : 'Review-report'}
                    size="xs"
                />
            </div>
        );
    }

    render() {
        const { review, canEdit } = this.props;
        const { reviewStatus } = review;
        const { isResponding, isReporting } = this.state;
        const showButtons = canEdit
            && !isReporting
            && !isResponding
            && reviewStatus !== ReviewsConstants.REVIEW_STATUS.OK_STARS_ONLY;
        return (
            <div className="Review">
                <div className="Review-container">
                    {this.renderReview()}
                    {isReporting && this.renderReportReviewForm()}
                    {isResponding && this.renderResponseForm()}
                    {showButtons && this.renderRespondButton()}
                    {showButtons && this.renderReportButton()}
                </div>
            </div>
        );
    }
}

Review.propTypes = propTypes;
Review.defaultProps = defaultProps;

export default Review;
